import { AppIcon, formatStripePlanPrice, formatStripePlanInterval } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { SubscriptionPlan } from '@backrs_ui/grow-ui/src/redux/subscription/subscriptionApi';

import { Typography, Box, SxProps, Theme } from '@mui/material';

import { PaymentPageProps } from '../SubscriptionPageContainer';

const STRINGS = appStrings.modules.subscription.subscriptionPlans;

const formatMarketingFeatures = (features: { name: string }[] | undefined): string | null => {
  if (!features || features.length === 0) return null;

  const featureNames = features.map((f) => f.name);

  if (featureNames.length === 1) {
    return `Includes ${featureNames[0]}.`;
  }

  if (featureNames.length === 2) {
    return `Includes ${featureNames[0]} and ${featureNames[1]}.`;
  }

  const allButLast = featureNames.slice(0, -1).join(', ');
  const last = featureNames[featureNames.length - 1];

  return `Includes ${allButLast}, and ${last}.`;
};

type PlanCardProps = Readonly<{
  plan: SubscriptionPlan;
  isSelected: boolean;
  loading?: boolean;
  onSelectPlan: (priceId: string) => void;
  subscriptionView: PaymentPageProps['subscriptionView'];
}>;

export const PlanCard = ({ plan, isSelected, loading, onSelectPlan }: PlanCardProps) => {
  const handleClick = () => {
    if (loading) return;

    onSelectPlan(plan.id);
  };

  const showBadge = plan.promoData;
  const showBadgeStars = plan.promoData?.type === 'free_trial';

  return (
    <Box id={`subscription-plan-${plan.id}`} sx={containerSx(isSelected)} onClick={handleClick}>
      {/* Header */}
      <Box sx={headerSx(isSelected)}>
        <Box sx={headerContentSx}>
          {/* Featured Icon */}
          <Box sx={featuredIconSx}>
            <AppIcon iconName="LayersTwo01" diameter="16px" color="Gray/700" />
          </Box>
          {/* Title */}
          <Typography variant="text-md-semibold" sx={titleSx}>
            {plan.name}
          </Typography>
        </Box>
        {/* Checkbox */}
        <Box sx={checkboxSx(isSelected)}>
          {isSelected && <AppIcon iconName="Check" diameter="12px" color="Colors/Base/White" />}
        </Box>
      </Box>

      {/* Body */}
      <Box sx={bodySx}>
        <Box sx={bodyContentSx}>
          {/* Price row */}
          <Box sx={priceRowSx}>
            {plan.promoData ? (
              <>
                {/* Promo display text */}
                <Typography variant="display-sm-semibold" sx={priceSx}>
                  {plan.promoData.displayText ?? formatStripePlanPrice(plan.amount, plan.currency)}
                </Typography>
                <Box sx={timePeriodSx}>
                  <Typography variant="text-sm-regular" sx={timePeriodTextSx}>
                    {plan.promoData.periodText ??
                      formatStripePlanInterval(plan.interval, plan.intervalCount)}
                  </Typography>
                </Box>
              </>
            ) : plan.amount === 0 ? (
              <Typography variant="display-sm-semibold" sx={priceSx}>
                {STRINGS.free}
              </Typography>
            ) : (
              <>
                {/* Regular price display */}
                <Typography variant="display-sm-semibold" sx={priceSx}>
                  {formatStripePlanPrice(plan.amount, plan.currency)}
                </Typography>
                <Box sx={timePeriodSx}>
                  <Typography variant="text-sm-regular" sx={timePeriodTextSx}>
                    {formatStripePlanInterval(plan.interval, plan.intervalCount)}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
          {/* Description */}
          <Typography variant="text-sm-regular" sx={descriptionSx}>
            {formatMarketingFeatures(plan.marketingFeatures) ?? plan.description}
          </Typography>
        </Box>

        {/* Badge */}
        {showBadge && (
          <Box sx={badgeContainerSx}>
            {showBadgeStars ? (
              <AppIcon iconName="Stars03" diameter="12px" color="Brand/600" />
            ) : (
              <Box sx={badgeDotSx} />
            )}
            <Typography variant="text-xs-medium" sx={badgeTextSx}>
              {formatStripePlanPrice(plan.amount, plan.currency)}/{plan.interval}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const containerSx =
  (isSelected: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    borderRadius: backrs.radius['radius-xl'],
    border: isSelected
      ? `2px solid ${backrs.colors['Brand/600']}`
      : `1px solid ${backrs.colors['Gray/200']}`,
    backgroundColor: backrs.colors['Colors/Base/White'],
    cursor: 'pointer',
    transition: 'border-color 0.15s ease-in-out',
    '&:hover': {
      borderColor: isSelected ? backrs.colors['Brand/600'] : backrs.colors['Gray/300'],
    },
  });

const headerSx =
  (isSelected: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: backrs.spacing['spacing-xs'],
    paddingLeft: backrs.spacing['spacing-lg'],
    paddingRight: backrs.spacing['spacing-2xl'],
    paddingY: backrs.spacing['spacing-lg'],
    borderBottom: isSelected
      ? `2px solid ${backrs.colors['Brand/600']}`
      : `1px solid ${backrs.colors['Gray/200']}`,
    borderTopLeftRadius: backrs.radius['radius-xl'],
    borderTopRightRadius: backrs.radius['radius-xl'],
  });

const headerContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xl'],
  flex: 1,
  minWidth: 0,
});

const featuredIconSx: SxProps<Theme> = ({ backrs }) => ({
  width: '32px',
  height: '32px',
  borderRadius: backrs.radius['radius-sm'],
  border: `1px solid ${backrs.colors['Gray/200']}`,
  backgroundColor: backrs.colors['Colors/Base/White'],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/700'],
});

const checkboxSx =
  (isSelected: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '16px',
    height: '16px',
    borderRadius: backrs.radius['radius-xs'],
    border: isSelected
      ? `1px solid ${backrs.colors['Brand/600']}`
      : `1px solid ${backrs.colors['Gray/300']}`,
    backgroundColor: isSelected ? backrs.colors['Brand/600'] : backrs.colors['Colors/Base/White'],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  });

const bodySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-xs'],
  padding: backrs.spacing['spacing-xl'],
});

const bodyContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  flex: 1,
  minWidth: 0,
});

const priceRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-end',
  gap: backrs.spacing['spacing-xs'],
});

const priceSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/700'],
  lineHeight: '38px',
});

const timePeriodSx: SxProps<Theme> = {
  paddingBottom: '3px',
};

const timePeriodTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/600'],
});

const descriptionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/600'],
});

const badgeContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
  paddingX: backrs.spacing['spacing-sm'],
  paddingY: backrs.spacing['spacing-xxs'],
  borderRadius: backrs.radius['radius-sm'],
  border: `1px solid ${backrs.colors['Gray/300']}`,
  backgroundColor: backrs.colors['Colors/Base/White'],
  flexShrink: 0,
});

const badgeDotSx: SxProps<Theme> = ({ backrs }) => ({
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  backgroundColor: backrs.colors['Success/500'],
});

const badgeTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/700'],
  whiteSpace: 'nowrap',
});
